// src/regression.js
// Response-driver regression for direct mail.
//
// Logistic regression (ridge-penalised, fit by IRLS) of "did this mailed lead
// respond?" on the things we know about the lead and the mail it got. Pure
// functions only — no React, no Supabase — so the regression tests in
// src/__tests__ can check the math directly.
//
// Honest-by-design: every factor carries a 95% interval and a plain label
// ("clear", "possible", "not enough data yet"). With ~100 responses only the
// strong effects can be trusted, and the UI says so.

// ── tiny linear algebra ──
function solveSym(A,b){ // Gaussian elimination with partial pivoting; A is n×n
  const n=A.length,M=A.map((r,i)=>r.concat([b[i]]));
  for(let c=0;c<n;c++){
    let p=c;for(let r=c+1;r<n;r++)if(Math.abs(M[r][c])>Math.abs(M[p][c]))p=r;
    [M[c],M[p]]=[M[p],M[c]];
    const d=M[c][c]||1e-12;
    for(let r=0;r<n;r++){if(r===c)continue;const f=M[r][c]/d;if(!f)continue;for(let k=c;k<=n;k++)M[r][k]-=f*M[c][k];}
  }
  return M.map((r,i)=>r[n]/(r[i]||1e-12));
}
function invert(A){const n=A.length;return A.map((_,j)=>solveSym(A,A.map((_,i)=>i===j?1:0))).reduce((inv,col,j)=>{col.forEach((v,i)=>{inv[i][j]=v;});return inv;},A.map(()=>new Array(n).fill(0)));}
const sigmoid=z=>z>=0?1/(1+Math.exp(-z)):Math.exp(z)/(1+Math.exp(z));

// Fit P(y=1)=sigmoid(X·b). X rows include a leading 1 (intercept, unpenalised).
// Returns {coef, se, iterations, converged}.
export function fitLogistic(X,y,opts){
  const lambda=(opts&&opts.lambda!=null)?opts.lambda:1;
  const p=X[0].length,n=X.length;
  let b=new Array(p).fill(0);
  const ybar=y.reduce((s,v)=>s+v,0)/n;
  b[0]=Math.log(Math.max(ybar,1e-6)/Math.max(1-ybar,1e-6));
  let H=null,converged=false,it=0;
  for(;it<50;it++){
    H=Array.from({length:p},()=>new Array(p).fill(0));
    const g=new Array(p).fill(0);
    for(let i=0;i<n;i++){
      const xi=X[i];let z=0;for(let k=0;k<p;k++)z+=xi[k]*b[k];
      const mu=sigmoid(z),w=Math.max(mu*(1-mu),1e-9),r=y[i]-mu;
      for(let j=0;j<p;j++){const xj=xi[j];if(!xj)continue;g[j]+=xj*r;const wx=w*xj;for(let k=j;k<p;k++)H[j][k]+=wx*xi[k];}
    }
    for(let j=0;j<p;j++){for(let k=0;k<j;k++)H[j][k]=H[k][j];if(j>0){H[j][j]+=lambda;g[j]-=lambda*b[j];}}
    const step=solveSym(H,g);
    let maxStep=0;for(let k=0;k<p;k++){b[k]+=step[k];maxStep=Math.max(maxStep,Math.abs(step[k]));}
    if(maxStep<1e-6){converged=true;it++;break;}
  }
  const cov=invert(H);
  return{coef:b,se:cov.map((r,i)=>Math.sqrt(Math.max(r[i],0))),iterations:it,converged:converged};
}
export function predictProb(coef,row){let z=0;for(let k=0;k<coef.length;k++)z+=coef[k]*row[k];return sigmoid(z);}

// Area under the ROC curve: chance a random responder is ranked above a
// random non-responder (0.5 = coin flip, 1 = perfect).
export function auc(scores,y){
  const idx=scores.map((s,i)=>i).sort((a,b)=>scores[a]-scores[b]);
  let rank=0,sumPos=0,nPos=0,i=0;
  while(i<idx.length){let j=i;while(j+1<idx.length&&scores[idx[j+1]]===scores[idx[i]])j++;const avg=(i+j)/2+1;for(let k=i;k<=j;k++){if(y[idx[k]]){sumPos+=avg;nPos++;}}i=j+1;rank=avg;}
  const nNeg=y.length-nPos;if(!nPos||!nNeg)return null;
  return (sumPos-nPos*(nPos+1)/2)/(nPos*nNeg);
}

// ── calendar days ──
// A calendar day is the LOCAL day ("YYYY-MM-DD"). Timestamps are stored as
// UTC ISO, so slicing the first 10 characters put every evening response /
// letter (5pm Pacific = 00:00Z) on the next day. localDay() converts an
// instant to the viewer's own day, and also reads the "M/D/YYYY" strings that
// toLocaleDateString() wrote into older mailings. Free text ("UNKNOWN (July
// 2026 handwritten)") is not a date and returns "".
const pad2=n=>String(n).padStart(2,"0");
const ymdOf=d=>d.getFullYear()+"-"+pad2(d.getMonth()+1)+"-"+pad2(d.getDate());
function validDay(y,m,d){if(m<1||m>12||d<1||d>31)return "";const dt=new Date(y,m-1,d,12);return(dt.getFullYear()===y&&dt.getMonth()===m-1&&dt.getDate()===d)?ymdOf(dt):"";}
export function localDay(v){
  if(!v)return "";
  if(v instanceof Date)return isNaN(v.getTime())?"":ymdOf(v);
  const s=String(v).trim();
  let r=/^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if(r)return validDay(+r[1],+r[2],+r[3]);
  r=/^(\d{4})-(\d{2})-(\d{2})[T ]\d/.exec(s);
  if(r){const d=new Date(s);return isNaN(d.getTime())?validDay(+r[1],+r[2],+r[3]):ymdOf(d);}
  r=/^(\d{1,2})[/-](\d{1,2})[/-](\d{4}|\d{2})$/.exec(s);
  if(r)return validDay(r[3].length===2?2000+(+r[3]):+r[3],+r[1],+r[2]);
  return "";
}
export function todayLocal(){return ymdOf(new Date());}

// ── lead → features ──
const RESPONDED_STATUS=["Responded","Consultation","Application"];
export function respondedOf(l){
  return !!(l.responded||l.converted_loan_id||RESPONDED_STATUS.indexOf(l.status)>=0||(l.mailings||[]).some(m=>m&&m.responded));
}
// Mailings that have actually gone out by `today` (a future-dated drop hasn't;
// an undated / free-text one is assumed sent).
function mailedOf(l,today){
  return (l.mailings||[]).filter(m=>{if(!m)return false;const d=localDay(m.sentAt||m.dateSent);return d?d<=today:true;});
}
// Was this lead ever actually mailed? Response-rate denominators use this so
// never-mailed leads can't sit in the "sent" column.
export function isMailedLead(l,today){return !!l&&mailedOf(l,today||todayLocal()).length>0;}
export function classOf(l){const t=(l.tags||[]).find(x=>/^class:/.test(x));return t?t.slice(6):"unknown";}
export function yearBucket(y){y=parseInt(y,10);if(!y)return "unknown";if(y<=2016)return "≤2016";if(y<=2019)return "2017–19";if(y<=2022)return "2020–22";return "2023+";}
export const CLASS_LABEL={refi_hecm:"HECM refi",refi_prop:"Proprietary refi",ftr:"First-time (FTR)",homesafe_second:"HomeSafe Second",refi_forward_loan:"Forward loan",refi_unknown_loan:"Unknown loan",unclassified:"Unclassified",unknown:"No product tag"};

// Rows for every lead that has actually been mailed.
// Non-responders whose latest mailing is under `recentDays` old are left out —
// they haven't had time to respond yet and would bias that drop downward.
export function mailedLeadRows(leads,today,recentDays){
  today=today||todayLocal();
  const settle=recentDays==null?21:recentDays;
  const cutoff=new Date(Date.parse(today+"T00:00:00Z")-settle*864e5).toISOString().slice(0,10);
  const out=[];
  (leads||[]).forEach(l=>{
    if(!l||l.doNotContact&&!respondedOf(l))return;
    const ms=mailedOf(l,today);if(!ms.length)return;
    const last=ms.map(m=>localDay(m.sentAt||m.dateSent)).filter(Boolean).sort().pop();
    if(!respondedOf(l)&&last&&last>cutoff)return;
    const hv=Number(l.home_value)||0,bal=Number(l.loan_balance)||0;
    out.push({
      y:respondedOf(l)?1:0,
      handwritten:ms.some(m=>/handwritten/i.test(m.method||""))?1:0,
      touches:ms.length>=3?"3+":String(ms.length),
      equity:hv>0?Math.max(-1,Math.min(1,(hv-bal)/hv)):null,
      logValue:hv>0?Math.log(hv):null,
      year:yearBucket(l.close_year||(String(l.origination_date||"").slice(0,4))),
      cls:classOf(l),
      state:String(l.state||"").toUpperCase()||"unknown",
    });
  });
  return out;
}

// Design spec: categorical levels with too few leads fold into "Other"; the
// most common level is the reference. Numeric features are standardised and
// get a "missing" flag.
export function buildDesign(rows,opts){
  const minLevel=(opts&&opts.minLevel)||300;
  const cats=["touches","year","cls","state"];
  const levels={};
  cats.forEach(c=>{
    const cnt={};rows.forEach(r=>{cnt[r[c]]=(cnt[r[c]]||0)+1;});
    let kept=Object.keys(cnt).filter(k=>cnt[k]>=minLevel).sort((a,b)=>cnt[b]-cnt[a]);
    // Baseline = the biggest REAL group ("unknown" makes a meaningless baseline).
    const ref=kept.find(k=>k!=="unknown")||kept[0];
    levels[c]={ref:ref,keep:kept.filter(k=>k!==ref).sort(),other:Object.keys(cnt).some(k=>cnt[k]<minLevel)};
  });
  const num={};
  ["equity","logValue"].forEach(f=>{
    const v=rows.map(r=>r[f]).filter(x=>x!=null);
    const m=v.reduce((s,x)=>s+x,0)/(v.length||1);
    const sd=Math.sqrt(v.reduce((s,x)=>s+(x-m)*(x-m),0)/Math.max(v.length-1,1))||1;
    num[f]={mean:m,sd:sd};
  });
  const cols=[{key:"(intercept)"},{key:"handwritten",label:"Handwritten letter (vs printed)",kind:"bin"}];
  cats.forEach(c=>{
    levels[c].keep.forEach(k=>cols.push({key:c+"="+k,cat:c,level:k,kind:"cat"}));
    if(levels[c].other)cols.push({key:c+"=Other",cat:c,level:"Other",kind:"cat"});
  });
  cols.push({key:"equity",label:"More equity (+1 SD)",kind:"num"},{key:"logValue",label:"Higher home value (+1 SD)",kind:"num"},{key:"valueMissing",label:"No value/balance on file",kind:"bin"});
  return{cols:cols,levels:levels,num:num};
}
export function encode(design,r){
  const {levels,num}=design;
  return design.cols.map(c=>{
    if(c.key==="(intercept)")return 1;
    if(c.key==="handwritten")return r.handwritten;
    if(c.kind==="cat"){
      const v=r[c.cat],L=levels[c.cat];
      const lvl=(v===L.ref||L.keep.indexOf(v)>=0)?v:"Other";
      return lvl===c.level?1:0;
    }
    if(c.key==="equity")return r.equity==null?0:(r.equity-num.equity.mean)/num.equity.sd;
    if(c.key==="logValue")return r.logValue==null?0:(r.logValue-num.logValue.mean)/num.logValue.sd;
    if(c.key==="valueMissing")return r.logValue==null?1:0;
    return 0;
  });
}

const CAT_NAME={touches:"Touches",year:"Loan year",cls:"Product",state:"State"};
export function factorLabel(c,design){
  if(c.label)return c.label;
  const ref=design.levels[c.cat].ref;
  const nice=v=>c.cat==="cls"?(CLASS_LABEL[v]||v):c.cat==="touches"?v+(v==="1"?" touch":" touches"):v;
  return CAT_NAME[c.cat]+": "+nice(c.level)+" (vs "+nice(ref)+")";
}

// Deterministic shuffle so the model check is repeatable.
function seededOrder(n,seed){let s=seed||12345;const a=Array.from({length:n},(_,i)=>i);for(let i=n-1;i>0;i--){s=(s*1103515245+12345)&0x7fffffff;const j=s%(i+1);[a[i],a[j]]=[a[j],a[i]];}return a;}

// Full analysis: fit on everything for the factor table, plus a 70/30
// holdout check (AUC + how much better the top 10% respond than average).
export function analyzeResponses(leads,opts){
  const rows=mailedLeadRows(leads,opts&&opts.today,opts&&opts.recentDays);
  const events=rows.reduce((s,r)=>s+r.y,0);
  if(rows.length<200||events<15)return{ok:false,rows:rows.length,events:events};
  const design=buildDesign(rows,opts);
  const X=rows.map(r=>encode(design,r)),y=rows.map(r=>r.y);
  const fit=fitLogistic(X,y,{lambda:1});
  const factors=design.cols.slice(1).map((c,i)=>{
    const k=i+1,b=fit.coef[k],se=fit.se[k],z=se?b/se:0;
    return{key:c.key,label:factorLabel(c,design),or:Math.exp(b),lo:Math.exp(b-1.96*se),hi:Math.exp(b+1.96*se),
      strength:Math.abs(z)>=1.96?"clear":Math.abs(z)>=1.28?"possible":"not enough data yet",n:X.reduce((s,row)=>s+(row[k]&&c.kind!=="num"?1:0),0)};
  }).sort((a,b)=>Math.abs(Math.log(b.or))-Math.abs(Math.log(a.or)));
  // holdout
  const order=seededOrder(rows.length,opts&&opts.seed),cut=Math.floor(rows.length*0.7);
  const tr=order.slice(0,cut),te=order.slice(cut);
  let check=null;
  if(te.some(i=>y[i])&&tr.some(i=>y[i])){
    const f2=fitLogistic(tr.map(i=>X[i]),tr.map(i=>y[i]),{lambda:1});
    const s=te.map(i=>predictProb(f2.coef,X[i])),yt=te.map(i=>y[i]);
    const ranked=te.map((i,k)=>({p:s[k],y:yt[k]})).sort((a,b)=>b.p-a.p);
    const top=ranked.slice(0,Math.max(1,Math.floor(ranked.length*0.1)));
    const base=yt.reduce((a,v)=>a+v,0)/yt.length;
    const topRate=top.reduce((a,v)=>a+v.y,0)/top.length;
    check={auc:auc(s,yt),lift:base?topRate/base:null,testLeads:te.length,testResponses:yt.reduce((a,v)=>a+v,0)};
  }
  return{ok:true,rows:rows.length,events:events,baseRate:events/rows.length,design:design,fit:fit,factors:factors,check:check,X:X,leadRows:rows};
}

// Forecast a planned drop: average predicted rate over historical leads that
// match product/state, with letter type and touch number set to the plan.
export function forecastDrop(analysis,plan){
  if(!analysis||!analysis.ok)return null;
  const {design,fit,leadRows}=analysis;
  let pool=leadRows.filter(r=>(!plan.cls||r.cls===plan.cls)&&(!plan.state||r.state===plan.state));
  const matched=pool.length;
  if(!pool.length)pool=leadRows;
  const rate=pool.reduce((s,r)=>s+predictProb(fit.coef,encode(design,Object.assign({},r,{handwritten:plan.handwritten?1:0,touches:plan.touches||r.touches}))),0)/pool.length;
  const n=Math.max(0,Math.round(plan.pieces||0));
  const exp=n*rate,sd=Math.sqrt(n*rate*(1-rate));
  return{rate:rate,expected:exp,low:Math.max(0,exp-1.96*sd),high:exp+1.96*sd,matched:matched,costPerResponse:exp>0&&plan.costPerPiece?(n*plan.costPerPiece)/exp:null};
}
